import cx from 'classnames';
import { formatDistanceToNowStrict } from 'date-fns';
import { has } from 'lodash-es';
import { motion } from 'motion/react';

import { Card, CardBody, Text } from 'components/@tedui';
import { MediumRectangle } from 'components/ads';
import { Link } from 'components/router';
import Trim from 'components/video-ads/trim';

import type { LINK_TYPE } from 'components/pages/homepage/HeroSlider';
import { ViewCount } from 'lib/business';
import { SailthruFormattedItem } from 'lib/sailthru';
import { abbreviateNumber } from 'lib/util';

import { cardVariants } from '../animations';

interface RibbonTalkProps {
  talk: SailthruFormattedItem;
  index: number;
  trackClick: (linkType: LINK_TYPE, itemNumber: number) => void;
  shouldShowAd: boolean;
  isAdLoading: boolean;
  isAdFilled: boolean;
  adTagId?: string;
  adCustomParams?: Record<string, string>;
  onAdFillChange: (isFilled: boolean) => void;
  shouldShowAdInRibbon: boolean;
}

export function RibbonTalk({
  talk,
  index,
  trackClick,
  shouldShowAd,
  isAdLoading,
  isAdFilled,
  adTagId,
  adCustomParams,
  onAdFillChange,
  shouldShowAdInRibbon
}: RibbonTalkProps) {
  return (
    <motion.div
      key={talk.slug}
      className="flex flex-col border-t-thin pt-6 lg-tui:first:border-t-[0] lg-tui:first:pt-0"
      variants={cardVariants}
    >
      <Card>
        <CardBody addHorizontalPadding={false} UNSAFE_className="!p-0">
          <Link
            href={`/talks/${talk.slug}`}
            onClick={() => trackClick('title', index)}
            isZenithLink
          >
            <Text
              tag="p"
              variant="subheader2"
              useNewTextStyles
              weight="font-semibold"
              UNSAFE_className="mb-2"
            >
              {talk.title}
            </Text>

            <div className="flex flex-col leading-tui-lg">
              <Text tag="p" variant="caption1" useNewTextStyles>
                {has(talk, 'viewedCount') && (
                  <ViewCount count={talk.viewedCount as number}>
                    {`${abbreviateNumber(talk.viewedCount as number)}`} plays
                    ·{' '}
                  </ViewCount>
                )}
                <>{talk.presenterDisplayName} · </>
                {has(talk, 'publishedAt') && (
                  <>
                    {formatDistanceToNowStrict(new Date(talk!.publishedAt))} ago
                  </>
                )}
              </Text>
            </div>
          </Link>
        </CardBody>
      </Card>

      {/* Ad Unit after talk 2 - Hidden on widths 808–1054px */}
      {shouldShowAd && (
        <motion.div
          className={cx(
            'mt-6 border-t-thick pt-6 [@media(min-width:1055px)]:!block [@media(min-width:808px)]:hidden',
            {
              // Show container when loading or filled
              block: isAdLoading || isAdFilled,
              // Hide container completely when not loading and not filled to allow layout recalculation
              hidden: !isAdLoading && !isAdFilled
            }
          )}
          variants={cardVariants}
        >
          <div
            className={cx(
              'relative flex w-full items-center justify-center transition-all duration-300',
              {
                // Show container styling while loading or filled
                'bg-gray-100 border rounded h-full border-gray-200':
                  isAdLoading || isAdFilled
              }
            )}
          >
            <Trim filled>
              {/* Show skeleton overlay while ad is loading and not yet filled */}
              {isAdLoading && !isAdFilled && (
                <div className="z-10 flex h-[250px] w-[300px] animate-pulse items-center justify-center bg-gray-200">
                  <div className="text-sm text-gray-400">Loading ad...</div>
                </div>
              )}

              {/* Always render the ad component - it will collapse if empty due to setCollapseEmptyDiv */}
              <MediumRectangle
                key={`ad-${shouldShowAdInRibbon ? 'visible' : 'hidden'}`}
                custParams={adCustomParams}
                path={adTagId || ''}
                isVideoAd={false}
                onFillChange={onAdFillChange}
              />
            </Trim>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
